import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { listBiosites, createBiosite, deleteBiosite, logout, THEMES } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Plus, LogOut, ExternalLink, Edit3, Trash2, Link2, Search, Loader2, Copy, Shield } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

export default function AdminDashboard() {
  const [biosites, setBiosites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const refresh = async () => {
    setLoading(true);
    try {
      const data = await listBiosites();
      setBiosites(data || []);
    } catch (e) {
      toast({ title: 'Erro ao carregar biosites.' });
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const doLogout = () => { logout(); navigate('/admin/login'); };

  const create = async (e) => {
    e.preventDefault();
    const s = slug.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
    if (!s || !name.trim()) { setError('Preencha todos os campos.'); return; }
    setCreating(true);
    setError('');
    try {
      await createBiosite({
        slug: s,
        name: name.trim(),
        bio: 'Edite sua bio aqui ✨',
        avatar: `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(name)}&backgroundType=gradientLinear`,
        theme: 'mono',
        verified: false,
        links: [], pix: [], socials: []
      });
      toast({ title: 'Biosite criado!', description: `/${s} está pronto para edição.` });
      setOpen(false); setSlug(''); setName(''); setError('');
      await refresh();
      navigate(`/admin/edit/${s}`);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Erro ao criar biosite.';
      setError(typeof msg === 'string' ? msg : 'Erro ao criar.');
    } finally { setCreating(false); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Excluir o biosite /${s}?`)) return;
    try {
      await deleteBiosite(s);
      toast({ title: 'Biosite excluído.' });
      await refresh();
    } catch (e) {
      toast({ title: 'Erro ao excluir.' });
    }
  };

  const copyLink = async (s) => {
    const url = `${window.location.origin}/${s}`;
    await navigator.clipboard.writeText(url);
    toast({ title: 'Link copiado!', description: url });
  };

  const filtered = biosites.filter(b =>
    b.name.toLowerCase().includes(search.toLowerCase()) ||
    b.slug.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#faf7f2]">
      <nav className="bg-white border-b border-neutral-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-2 font-display font-bold">
            <span className="w-8 h-8 rounded-lg bg-neutral-900 text-white grid place-items-center">
              <Link2 size={16} />
            </span>
            Biolink•br <span className="text-xs font-normal text-neutral-500 ml-1">admin</span>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/admin/settings">
              <Button variant="outline" className="gap-2 border-neutral-300"><Shield size={14} /> Segurança</Button>
            </Link>
            <Button variant="outline" onClick={doLogout} className="gap-2 border-neutral-300">
              <LogOut size={14} /> Sair
            </Button>
          </div>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-display font-bold tracking-tight">Biosites dos clientes</h1>
            <p className="text-neutral-600 mt-1">Crie, edite e gerencie todas as páginas.</p>
          </div>
          <Button onClick={() => setOpen(true)} className="gap-2 bg-neutral-900 hover:bg-neutral-800 text-white h-11">
            <Plus size={16} /> Novo biosite
          </Button>
        </div>

        <div className="relative max-w-sm mb-6">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar..." className="pl-9 bg-white" />
        </div>

        {loading ? (
          <div className="grid place-items-center py-20"><Loader2 className="animate-spin text-neutral-400" /></div>
        ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((b) => {
            const theme = THEMES[b.theme] || THEMES.mono;
            return (
              <div key={b.slug} className="bg-white rounded-2xl border border-neutral-200 overflow-hidden group hover:shadow-md transition">
                <div className="h-16" style={{ background: theme.bg }} />
                <div className="px-4 pb-4 -mt-7">
                  <img src={b.avatar} className="w-14 h-14 rounded-full object-cover border-4 border-white" alt="" />
                  <div className="mt-2">
                    <div className="font-display font-bold">{b.name}</div>
                    <div className="text-xs text-neutral-500">/{b.slug}</div>
                  </div>
                  <div className="flex gap-2 mt-3 text-xs text-neutral-600">
                    <span className="bg-neutral-100 rounded-full px-2 py-0.5">{b.links.length} links</span>
                    <span className="bg-neutral-100 rounded-full px-2 py-0.5">{b.pix?.length || 0} pix</span>
                  </div>
                  <div className="mt-4 grid grid-cols-[1fr_auto_auto_auto] items-center gap-2">
                    <Link to={`/admin/edit/${b.slug}`}>
                      <Button size="sm" className="w-full gap-1.5 bg-neutral-900 hover:bg-neutral-800 text-white">
                        <Edit3 size={14} /> Editar
                      </Button>
                    </Link>
                    <Button size="sm" variant="outline" onClick={() => copyLink(b.slug)} className="border-neutral-300" title="Copiar link público">
                      <Copy size={14} />
                    </Button>
                    <a href={`/${b.slug}`} target="_blank" rel="noopener noreferrer">
                      <Button size="sm" variant="outline" className="border-neutral-300" title="Abrir página pública">
                        <ExternalLink size={14} />
                      </Button>
                    </a>
                    <Button size="sm" variant="outline" onClick={() => remove(b.slug)} className="border-neutral-300 text-red-600 hover:bg-red-50 hover:text-red-700" title="Excluir">
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="col-span-full text-center text-neutral-500 bg-white rounded-2xl py-16 border border-dashed border-neutral-300">
              Nenhum biosite encontrado.
            </div>
          )}
        </div>
        )}
      </main>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="font-display">Novo biosite</DialogTitle>
            <DialogDescription>Escolha uma URL única para o cliente.</DialogDescription>
          </DialogHeader>
          <form onSubmit={create} className="space-y-4">
            <div>
              <Label htmlFor="name">Nome de exibição</Label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex: João Silva" className="mt-1" />
            </div>
            <div>
              <Label htmlFor="slug">URL do biosite</Label>
              <div className="flex items-center mt-1">
                <span className="bg-neutral-100 border border-neutral-200 border-r-0 rounded-l-md px-3 py-2 text-sm text-neutral-500">biolink.br/</span>
                <Input id="slug" value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="joaosilva" className="rounded-l-none" />
              </div>
              <p className="text-xs text-neutral-500 mt-1">Apenas letras minúsculas e números.</p>
            </div>
            {error && <div className="text-sm text-red-600">{error}</div>}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={creating}>Cancelar</Button>
              <Button type="submit" disabled={creating} className="bg-neutral-900 hover:bg-neutral-800 text-white gap-2">
                {creating && <Loader2 className="animate-spin" size={14} />} Criar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
